import { memo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Badge, Box, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { BomFlowNodeData } from '../../contexts/types';

/**
 * BomCreateFlowNodeの固定幅・高さ（px）。
 * BomCreateModal.tsx側の枠計算（buildGroupNodes）・新規Node配置（addNode）で実サイズと一致させる必要があるため、
 * サイズの正としてここで定義しexportする。値を変更する場合は下部のBox w/hも合わせて変更すること。
 */
export const BOM_NODE_WIDTH = 160;
export const BOM_NODE_HEIGHT = 84;

/** BomCreateFlowNodeが表示用に受け取るデータ。level/hasCodeConflictは親(BomCreateModal)側でグラフ全体から算出し注入する（詳細は詳細設計02章§4.1） */
export interface BomCreateFlowNodeData extends BomFlowNodeData {
  /** 算出済みのLevel */
  level: number;
  /** Process Code重複時の赤枠表示フラグ */
  hasCodeConflict: boolean;
}

/**
 * 親(BomCreateModal)がAlertDialog開閉などの無関係なstate更新で再レンダーされても、
 * data/selectedの参照が変わらない限り再描画をスキップするためmemo化している。
 * ノード数が多いBOMで確認ダイアログ表示時のもたつきを防ぐ目的。
 */
export const BomCreateFlowNode = memo(function BomCreateFlowNode({ data, selected }: NodeProps<BomCreateFlowNodeData>) {
  const { t } = useTranslation();
  const bl = 'features.InvestmentScreening.BomCreateModal';

  return (
    <Box
      border="2px solid"
      borderColor={data.hasCodeConflict ? 'red.500' : selected ? 'teal.500' : 'gray.300'}
      borderRadius="md"
      bg="white"
      // 幅・高さを固定し、整列時のグループ枠計算（BomCreateModal.tsx側）と実サイズを一致させる。
      // 高さが可変だと兄弟ノードを囲む枠の上下マージンが揃わず、枠内で要素が中央に見えなくなるため固定にしている。
      // ※ Badge + 品名 + 品番の3行が収まる高さ。値自体は上記でexportしているBOM_NODE_WIDTH/BOM_NODE_HEIGHTが正。
      w={`${BOM_NODE_WIDTH}px`}
      h={`${BOM_NODE_HEIGHT}px`}
      overflow="hidden"
      boxShadow={selected ? 'md' : 'sm'}
      p={2}
    >
      <Handle type="target" position={Position.Left} />

      <Badge colorScheme="teal" fontSize="0.65em" mb={1}>
        {`Lv${data.level}`}
      </Badge>
      <Text fontWeight="bold" fontSize="sm" isTruncated title={data.itemName}>
        {data.itemName || t(`${bl}.label.unset`)}
      </Text>
      <Text fontSize="xs" color="gray.600" isTruncated title={data.itemNo}>
        {data.itemNo || '-'}
      </Text>

      <Handle type="source" position={Position.Right} />
    </Box>
  );
});
